<template>
  <div id="status-container" class='row light-border p-2'>
    <div class="col-5 ml-3">
      <Allsky :telescope="telescope"></Allsky>
    </div>
    <div class="col"></div>
    <div class="col-6 ml-auto mr-3">
      <div class="row pb-1">
        <div class="col">
          <strong>You are logged in to observe on the {{ telescope.name }}</strong>
        </div>
      </div>
      <ul class="list-group">
        <status-item
            :context="sessionInfo.context"
            :status-key="'Session'"
            :status-value="sessionInfo.value">
        </status-item>
      </ul>
      <div class="row pb-3 pt-2">
        <div class="col">
          <div class="progress" style="background: white">
            <div class="progress-bar" role="progressbar" v-bind:style="'width:' + percentComplete + '%'"
                 :aria-valuenow="percentComplete" aria-valuemin="0" aria-valuemax="100"></div>
          </div>
          <span>{{ minutesRemaining }} minutes remaining</span>
        </div>
      </div>
      <ul class="list-group">
        <status-item
            :context="availabilityInfo.context"
            :status-key="'Observatory'"
            :status-value="availabilityInfo.value">
        </status-item>
        <status-item
            :context="telescopeInfo.context"
            :status-key="'Telescope'"
            :status-value="telescopeInfo.value">
        </status-item>
        <status-item
            :context="cameraInfo.context"
            :status-key="'Camera'"
            :status-value="cameraInfo.value"
            :exposure-time="exposureSecondsLeft">
        </status-item>
        <status-item
            :context="progressInfo.context"
            :status-key="'Progress'"
            :status-value="progressInfo.value">
        </status-item>
      </ul>
    </div>
  </div>
</template>

<script>
  import moment from 'moment';

  import 'rti.css';
  import Allsky from 'allsky.vue';
  import StatusItem from 'statusItem.vue';
  import {
    siteStates,
    telescopeStates,
    cameraStates,
    progressStates
  } from '../js/common.js';

  export default {
    name: 'statusPanel',
    props: ['agentStatus', 'utcNow', 'telescope', 'session', 'exposureTimes', 'sessionActive'],
    components: {
      StatusItem,
      Allsky
    },
    data: function() {
      return {
        exposureSecondsLeft: 0
      };
    },
    computed: {
      minutesRemaining: function() {
        return moment.duration(this.activeSessionRemainSec, 'seconds').asMinutes().toFixed(0);
      },
      sessionLengthInMinutes: function() {
        return (this.session.activeSessionEnd - this.session.activeSessionStart) / 60;
      },
      percentComplete: function() {
        return (this.sessionLengthInMinutes - this.minutesRemaining) / this.sessionLengthInMinutes * 100;
      },
      sessionInfo: function() {
        if (this.utcNow < this.session.activeSessionStart)
          return {value: 'Not yet started', context: 'list-group-item-warning'};
        else if (this.utcNow >= this.session.activeSessionEnd)
          return {value: 'Ended', context: 'list-group-item-warning'};
        else if (this.utcNow < this.session.activeSessionEnd && this.utcNow >= this.session.activeSessionStart)
          return {value: 'In progress', context: 'list-group-item-success'};
        else
          return {value: 'Information unavailable', context: 'list-group-item'};
      },
      availabilityInfo: function() {
        if (!this.agentStatus.availability)
          return {value: 'Status unavailable', context: 'list-group-item-light'};

        let value = this.agentStatus.availability;

        if (value === siteStates.AVAILABLE)
          return {value: value, context: 'list-group-item-success'};
        else if (value === siteStates.SUN_UP || value === siteStates.TECHNICAL_FAILURE ||
          value === siteStates.BAD_WEATHER || value === siteStates.MAINTENANCE)
          return {value: value, context: 'list-group-item-danger'};
        else
          return {value: value, context: 'list-group-item'};
      },
      telescopeInfo: function() {
        if (!this.sessionActive || !this.agentStatus.telescope)
          return {value: 'Status unavailable', context: 'list-group-item-light'};

        let value = this.agentStatus.telescope;

        if (value === telescopeStates.STOPPED)
          return {value: value, context: 'list-group-item-success'};
        else if (value === telescopeStates.TRACKING || value === telescopeStates.NEW_SOURCE ||
          value === telescopeStates.MOVING_TO_TARGET || value === telescopeStates.STOPPING)
          return {value: value, context: 'list-group-item-warning'};
        else
          return {value: value, context: 'list-group-item'};
      },
      cameraInfo: function() {
        if (!this.sessionActive || !this.agentStatus.instrument)
          return {value: 'Status unavailable', context: 'list-group-item-light'};

        let value = this.agentStatus.instrument.replace('_', ' ');

        if (value === cameraStates.IDLE)
          return {value: value, context: 'list-group-item-success'};
        else if (value === cameraStates.EXPOSING || value === cameraStates.READING_OUT ||
          value === cameraStates.CONFIGURING || value === cameraStates.STOPPING || value === cameraStates.INIT)
          return {value: value, context: 'list-group-item-warning'};
        else
          return {value: value, context: 'list-group-item'};
      },
      progressInfo: function() {
        if (!this.sessionActive || !this.agentStatus.progress)
          return {value: 'Status unavailable', context: 'list-group-item-light'};

        let value = this.agentStatus.progress;

        if (value === progressStates.READY)
          return {value: value, context: 'list-group-item-success'};
        else if (value === progressStates.EXECUTING)
          return {value: value, context: 'list-group-item-warning'};
        else if (value === progressStates.ERROR)
          return {value: value, context: 'list-group-item-danger'};
        else
          return {value: value, context: 'list-group-item'};
      },
      activeSessionRemainSec: function() {
        if (this.session.activeSessionStart > this.utcNow)
          return (this.session.activeSessionEnd - this.session.activeSessionStart);
        else {
          let remainingSec = this.session.activeSessionEnd - this.utcNow;
          return (remainingSec > 0 ? remainingSec : 0);
        }
      }
    },
    watch: {
      cameraInfo: function() {
        if (this.lastCameraStatus !== cameraStates.EXPOSING && this.cameraInfo.value === cameraStates.EXPOSING) {
          this.exposureSecondsLeft = this.exposureTimes.shift() || 0;
        }
        if (this.cameraInfo.value !== cameraStates.EXPOSING) {
          this.exposureSecondsLeft = 0;
        }
        this.lastCameraStatus = this.cameraInfo.value;
      },
      utcNow: function() {
        if (this.exposureSecondsLeft > 0) {
          this.exposureSecondsLeft--;
        }
      }
    }
  };
</script>
